// ── useActivation: client hook for node activation management ─
// Wraps the /api/activation/* and /api/dream/* endpoints.

import { useCallback } from 'react';

export default function useActivation({ sessionId, authFetch }) {
  const setActivation = useCallback(async (nodeId, { threshold, reactions, refractoryPeriod, webhookUrl }) => {
    const res = await authFetch('/api/activation/set', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionId, nodeId, threshold, reactions, refractoryPeriod, webhookUrl }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'Failed to set activation' }));
      throw new Error(err.error);
    }
    return res.json();
  }, [sessionId, authFetch]);

  const pauseAllActivations = useCallback(async () => {
    const res = await authFetch('/api/activation/pause', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionId }),
    });
    if (!res.ok) throw new Error('Failed to pause activations');
    return res.json();
  }, [sessionId, authFetch]);

  const resumeAllActivations = useCallback(async () => {
    const res = await authFetch('/api/activation/resume', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionId }),
    });
    if (!res.ok) throw new Error('Failed to resume activations');
    return res.json();
  }, [sessionId, authFetch]);

  const getActivationPulse = useCallback(async () => {
    const res = await authFetch(`/api/activation/${sessionId}/pulse`);
    if (!res.ok) throw new Error('Failed to load activation pulse');
    return res.json();
  }, [sessionId, authFetch]);

  const getActivationStatus = useCallback(async () => {
    const res = await authFetch(`/api/activation/${sessionId}/status`);
    if (!res.ok) throw new Error('Failed to load activation status');
    return res.json();
  }, [sessionId, authFetch]);

  // Dream state
  const enableDreamState = useCallback(async (schedule) => {
    const res = await authFetch('/api/dream/enable', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionId, schedule }),
    });
    if (!res.ok) throw new Error('Failed to enable dream state');
    return res.json();
  }, [sessionId, authFetch]);

  const disableDreamState = useCallback(async () => {
    const res = await authFetch('/api/dream/disable', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionId }),
    });
    if (!res.ok) throw new Error('Failed to disable dream state');
    return res.json();
  }, [sessionId, authFetch]);

  const runDreamNow = useCallback(async () => {
    const res = await authFetch('/api/dream/run', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionId }),
    });
    if (!res.ok) throw new Error('Failed to run dream cycle');
    return res.json();
  }, [sessionId, authFetch]);

  const getDreamReports = useCallback(async () => {
    const res = await authFetch(`/api/dream/${sessionId}/reports`);
    if (!res.ok) throw new Error('Failed to load dream reports');
    return res.json();
  }, [sessionId, authFetch]);

  const getDreamStatus = useCallback(async () => {
    const res = await authFetch(`/api/dream/${sessionId}/status`);
    if (!res.ok) throw new Error('Failed to load dream status');
    return res.json();
  }, [sessionId, authFetch]);

  return {
    setActivation,
    pauseAllActivations,
    resumeAllActivations,
    getActivationPulse,
    getActivationStatus,
    enableDreamState,
    disableDreamState,
    runDreamNow,
    getDreamReports,
    getDreamStatus,
  };
}
